import i18n from '@/locales/i18n';
import { DocumentCategory } from '../types';

// Formats supportés
export const SUPPORTED_FILE_TYPES = [
  // Images
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  // Documents
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  // Archives
  'application/zip',
  'application/x-zip-compressed',
  'application/x-rar-compressed',
  // Mardown and text files
  'text/markdown',
  'text/plain',
  'text/html',
  'text/css',
  'text/javascript',
  'text/xml',
  'text/json',
  'text/yaml',
  'text/toml',
  'text/csv',
  'text/tab-separated-values',
];

// Taille maximale (10 MB par défaut)
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

/**
 * Format bytes to human readable string
 */
export const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * Get icon for mime type
 */
export const getMimeTypeIcon = (mimeType: string): string => {
  if (mimeType.startsWith('image/')) return '🖼️';
  if (mimeType.startsWith('application/pdf')) return '📄';
  if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet'))
    return '📊';
  if (mimeType.includes('zip') || mimeType.includes('rar')) return '📦';
  if (mimeType.startsWith('text/')) return '📃';
  return '📎';
};

/**
 * Validate file before upload
 */
export const validateFile = (
  file: File
): { valid: boolean; error?: string } => {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `Le fichier est trop volumineux. Taille maximale: ${formatBytes(MAX_FILE_SIZE)}`,
    };
  }

  // Check file type
  if (!SUPPORTED_FILE_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: "Ce type de fichier n'est pas supporté",
    };
  }

  return { valid: true };
};

/**
 * Get category label
 */
const CATEGORY_LABEL_FALLBACK: Record<DocumentCategory, string> = {
  'bulletin-de-paie': 'Bulletin de paie',
  dsn: 'DSN',
  contrat: 'Contrat',
  attestation: 'Attestation',
  facture: 'Facture',
  'document-rh': 'Document RH',
  reglementaire: 'Réglementaire',
  identifiant: 'Identifiant',
  'document-texte': 'Document texte',
  autre: 'Autre',
};

export const getCategoryLabel = (category: DocumentCategory): string => {
  const translationKey = `documents.categories.${category}`;
  const translated = i18n.t(translationKey, {
    defaultValue: CATEGORY_LABEL_FALLBACK[category] ?? category,
  });

  // i18next returns the defaultValue even if the key is missing
  // but we safeguard to ensure string is returned
  if (!translated || translated === translationKey) {
    return CATEGORY_LABEL_FALLBACK[category] ?? category;
  }

  return translated;
};

/**
 * Check if file type can be previewed
 */
export const canPreview = (mimeType: string): boolean => {
  return mimeType.startsWith('image/') || mimeType === 'application/pdf';
};

/**
 * Generate file name with timestamp
 */
export const generateFileName = (originalName: string): string => {
  const timestamp = Date.now();
  const extension = originalName.split('.').pop();
  const nameWithoutExt = originalName.replace(`.${extension}`, '');
  return `${nameWithoutExt}_${timestamp}.${extension}`;
};
